import { useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertOctagon, AlertTriangle, Info, Megaphone, X } from 'lucide-react';
import { ApiError, api } from '../lib/api.js';
import { ago } from '../lib/format.js';
import { Button, Dialog, DialogContent, DialogTrigger, Field, Input, Select, Switch, cn, useToast } from '../ui/index.js';
import { MarkdownBody } from './MarkdownBody.js';
import { RichTextEditor } from './RichTextEditor.js';

export interface Bulletin {
  id: string;
  campaignId: string | null;
  title: string;
  body: string;
  severity: 'info' | 'warning' | 'critical';
  audience: 'desk' | 'everyone';
  /** Added to the desk's emails to requesters (077). */
  inEmails: boolean;
  expiresAt: string | null;
  createdAt: string;
  author: string | null;
}

const key = (tenant: string) => ['bulletins', tenant];

const TONE = {
  critical: { box: 'border-danger-line/70 bg-danger-tint/50', icon: <AlertOctagon className="h-4 w-4 text-danger" />, label: 'Critical' },
  warning: { box: 'border-warn-line/70 bg-warn-tint/50', icon: <AlertTriangle className="h-4 w-4 text-warn" />, label: 'Warning' },
  info: { box: 'border-info/40 bg-info-tint/50', icon: <Info className="h-4 w-4 text-info" />, label: 'Notice' },
} as const;

/** Hidden by this viewer, by id, in this browser. Critical ones cannot be hidden. */
const HIDDEN_KEY = 'esa.bulletins.hidden';
function readHidden(): string[] {
  try {
    return JSON.parse(localStorage.getItem(HIDDEN_KEY) ?? '[]') as string[];
  } catch {
    return [];
  }
}

/**
 * The group's live bulletins, at the top of a page (076). The desk sees all
 * of them; a requester sees those meant for everyone. Whoever may post one
 * can take it down from here.
 */
export function useBulletins(tenant: string) {
  return useQuery({
    queryKey: key(tenant),
    queryFn: () => api.get<{ bulletins: Bulletin[]; canPost: boolean }>(`/api/v1/tenants/${tenant}/bulletins`),
    enabled: Boolean(tenant),
    refetchInterval: 120_000,
    // A group someone cannot read bulletins in is not an error worth showing.
    retry: false,
  });
}

/**
 * Take a bulletin down, for everybody and off the emails. The one way to end
 * a bulletin before it expires, so it is offered wherever one is managed.
 */
export function useTakeDownBulletin(tenant: string) {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: (id: string) => api.del(`/api/v1/tenants/${tenant}/bulletins/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: key(tenant) });
      toast.success('Bulletin taken down', 'Gone for everyone, and no longer added to emails.');
    },
    onError: (e) => toast.error('Not taken down', e instanceof ApiError ? e.message : undefined),
  });
}

export function BulletinBoard({ tenant, className }: { tenant: string; className?: string }) {
  const q = useBulletins(tenant);
  const [hidden, setHidden] = useState<string[]>(readHidden);
  const remove = useTakeDownBulletin(tenant);
  /**
   * NO "HIDE FOR ME" FOR WHOEVER CAN TAKE IT DOWN. Hiding is per browser, and
   * it took the only Take down button with it: the poster hid the banner,
   * thought it was gone, and it went on being shown to everyone else and
   * added to every email. For them the one control is the real one.
   */
  const canTakeDown = Boolean(q.data?.canPost);
  const hide = (id: string) => {
    const next = [...hidden, id];
    setHidden(next);
    try {
      localStorage.setItem(HIDDEN_KEY, JSON.stringify(next.slice(-50)));
    } catch {
      // A browser that will not store it just shows it again next time.
    }
  };

  const shown = (q.data?.bulletins ?? []).filter((b) => canTakeDown || b.severity === 'critical' || !hidden.includes(b.id));
  if (!shown.length) return null;
  return (
    <div className={cn('space-y-2', className)} role="region" aria-label="Bulletins">
      {shown.map((b) => {
        const t = TONE[b.severity];
        return (
          <div key={b.id} className={cn('rounded-md border px-3.5 py-2.5', t.box)}>
            <div className="flex items-start gap-2.5">
              <span className="mt-0.5 shrink-0" aria-label={t.label}>
                {t.icon}
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-ink">{b.title}</p>
                {b.body && <MarkdownBody text={b.body} className="mt-1 text-xs" />}
                <p className="mt-1.5 text-2xs text-ink-tertiary">
                  {b.author ? `${b.author}, ` : ''}
                  {ago(b.createdAt)}
                  {b.audience === 'desk' ? ' · desk only' : ' · shown to everyone'}
                  {b.inEmails ? ' · added to emails' : ''}
                  {b.expiresAt ? ` · until ${new Date(b.expiresAt).toLocaleDateString()}` : ''}
                </p>
              </div>
              <span className="flex shrink-0 items-center gap-1">
                {canTakeDown && (
                  <Button variant="ghost" size="sm" disabled={remove.isPending} onClick={() => remove.mutate(b.id)}>
                    Take down
                  </Button>
                )}
                {!canTakeDown && b.severity !== 'critical' && (
                  <Button variant="ghost" size="sm" iconOnly aria-label="Hide for me" title="Hide for me" onClick={() => hide(b.id)}>
                    <X className="h-3.5 w-3.5" />
                  </Button>
                )}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

const inDays = (d: number) => {
  const t = new Date(Date.now() + d * 86_400_000);
  return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
};

/** Write a bulletin, optionally about a campaign; prefilled by the caller. */
export function PostBulletin({
  tenant,
  trigger,
  campaignId,
  replaces,
  initial,
}: {
  tenant: string;
  trigger: ReactNode;
  campaignId?: string;
  /** The title of the campaign's live bulletin, which posting this replaces. */
  replaces?: string | null;
  initial?: { title?: string; body?: string };
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState(initial?.title ?? '');
  const [body, setBody] = useState(initial?.body ?? '');
  const [severity, setSeverity] = useState<'info' | 'warning' | 'critical'>('warning');
  const [audience, setAudience] = useState<'desk' | 'everyone'>('everyone');
  const [until, setUntil] = useState(inDays(7));
  const [inEmails, setInEmails] = useState(true);
  const post = useMutation({
    mutationFn: () =>
      api.post(`/api/v1/tenants/${tenant}/bulletins`, {
        title: title.trim(),
        body,
        severity,
        audience,
        inEmails: audience === 'everyone' && inEmails,
        // To the end of the chosen day, local time.
        expiresAt: until ? new Date(`${until}T23:59:59`).toISOString() : null,
        ...(campaignId ? { campaignId } : {}),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: key(tenant) });
      toast.success('Bulletin posted', audience === 'everyone' ? 'Shown to the desk and to requesters.' : 'Shown to the desk.');
      setOpen(false);
    },
    onError: (e) => toast.error('Not posted', e instanceof ApiError ? e.message : undefined),
  });
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent
        title={replaces ? 'Replace the bulletin' : 'Post a bulletin'}
        description={
          replaces
            ? `This campaign already has a bulletin, “${replaces}”. Posting this takes that one down, so people see one notice about it, not two.`
            : 'A notice at the top of every page and, for everyone, on the desk’s emails to requesters. It shows until it expires or is taken down.'
        }
        width="lg"
      >
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (title.trim()) post.mutate();
          }}
        >
          <Field label="Title" required>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={160} />
          </Field>
          <Field label="What people should know and do">
            <RichTextEditor ariaLabel="Bulletin text" value={body} onChange={setBody} />
          </Field>
          <div className="grid gap-3 sm:grid-cols-3">
            <Field label="Severity">
              <Select
                value={severity}
                onValueChange={(v) => setSeverity(v as typeof severity)}
                options={[
                  { value: 'info', label: 'Notice' },
                  { value: 'warning', label: 'Warning' },
                  { value: 'critical', label: 'Critical (cannot be hidden)' },
                ]}
                aria-label="Severity"
              />
            </Field>
            <Field label="Who sees it">
              <Select
                value={audience}
                onValueChange={(v) => setAudience(v as typeof audience)}
                options={[
                  { value: 'everyone', label: 'Everyone' },
                  { value: 'desk', label: 'The desk only' },
                ]}
                aria-label="Who sees it"
              />
            </Field>
            <Field label="Show until" hint="Empty: until taken down">
              <Input type="date" value={until} onChange={(e) => setUntil(e.target.value)} />
            </Field>
          </div>
          {audience === 'everyone' && (
            <Switch
              checked={inEmails}
              onCheckedChange={setInEmails}
              label="Add it to the desk's emails to requesters while it shows (replies, acknowledgements, answers)"
            />
          )}
          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="ghost" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" leading={<Megaphone className="h-3.5 w-3.5" />} loading={post.isPending} disabled={!title.trim()}>
              Post bulletin
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
